import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { devRequestService } from "../services/dataService";
import { DevRequestAttachment, DevRequestDetail, DevRequestEvent, DevRequestEventKind as K, DevRequestImpact as Im, DevRequestListItem, DevRequestStatus as S, DevRequestType as T } from "../types";
import { usePermissions } from "../context/PermissionContext";
import {
  DR_ACCEPT, DR_FILE_MSG, DR_IMPACT_LABEL, DR_MAX_FILES, DR_MAX_TOTAL, DR_PRIORITY_BADGE, DR_PRIORITY_LABEL, DR_REOPEN_DAYS,
  DR_STATUS_CLASS, DR_STATUS_HINT, DR_STATUS_LABEL, DR_STEPS, DR_TYPE, DR_VENDOR_MANAGED, DR_WAIT_DAYS, daysSince, drCheckFile, drDaysUntil, drErr,
  drFmtDate, drFmtDateTime, drHue, drInitials, drIsOpen, drLayerBlocked, drPastedFiles, drShortName, drStepIndex, drTransitionLabel,
} from "../utils/devRequests";
import { relTime } from "./NotificationBell";
import { screenPath } from "./ScreenPicker";
import { DrAttachmentGrid, DrLightbox, DrUploading } from "./DevRequestFiles";
import ConfirmModal from "./ConfirmModal";
import "../pages/PurchaseOrders.css";
import "../pages/DevRequests.css";

/**
 * Geliştirme talebi kartı (çekmece). Talep sahibinin geri çekme/yeniden açma, düzenleme ve silme işlemlerini
 * sayfadan ister (onay, bildirim ve yenileme tek yerde); yazışma ve dosya yükleme/kaldırma burada yapılır, sonra
 * onChanged. Durum ve hedef tarih yalnız Nedaris Destek'ten gelir: yöneticiye salt okunur not gösterilir.
 */
export interface DevRequestDrawerProps {
  id: string;
  /** Liste satırı: ayrıntı yüklenirken başlık anında dolar. */
  preview?: DevRequestListItem;
  /** Sayfa her değişiklikten sonra artırır → ayrıntı yeniden çekilir. */
  refreshKey: number;
  escDisabled?: boolean;
  /** Talep başına yanıt taslağı (sayfa durumunda yaşar): çekmece kapanınca / satır değişince kaybolmaz. */
  drafts: Map<string, { text: string; internal: boolean }>;
  onClose: () => void;
  /** Yazışma/dosya değişikliği ya da 404 sonrası: sayfa listeyi yeniler, refreshKey'i artırır, msg varsa bildirir. */
  onChanged: (msg?: string) => void;
  onEdit: (d: DevRequestDetail) => void;
  /** Talep sahibinin işlemi: geri çek ya da yeniden aç. */
  onAction: (d: DevRequestDetail, to: S.Withdrawn | S.InReview) => void;
  onDelete: (d: DevRequestDetail) => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>,
  reopen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>,
  withdraw: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></svg>,
  clip: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
};

const NOT_FOUND = "Talep bulunamadı; silinmiş olabilir. Liste yenilendi.";
const CLOSED_FILES = "Kapanmış talebe dosya eklenemez.";
const MSG_MAX = 4000;
const PRE: React.CSSProperties = { whiteSpace: "pre-wrap", overflowWrap: "anywhere" };
const DROP_OVER: React.CSSProperties = { outline: "2px dashed var(--po-navy)", outlineOffset: "0.35rem", borderRadius: "0.5rem" };
const H3_BTN: React.CSSProperties = { marginLeft: "auto", textTransform: "none", letterSpacing: 0, fontSize: "0.76rem" };

type Draft = { id: string; text: string; internal: boolean };
type Job = DrUploading & { reqId: string; size: number };
let upSeq = 0;

const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).indexOf("Files") >= 0;
/** Yükleme hatası: IIS'in 413 sayfası (HTML) kullanıcıya gösterilmez. */
const upErr = (e: any) => {
  if (e?.response?.status === 413) return DR_FILE_MSG.size;
  const d = e?.response?.data;
  if (typeof d === "string" && /^\s*</.test(d)) return "Dosya yüklenemedi.";
  return drErr(e, "Dosya yüklenemedi.");
};
const startsLower = (s: string) => { const c = s.charAt(0); return !!c && c !== c.toLocaleUpperCase("tr-TR"); };
const when = (iso: string) => <time dateTime={iso} title={drFmtDateTime(iso)}>{relTime(iso)}</time>;

/**
 * Sistem/kayıt olayının satır metni (yorum dışı türler). Sunucu Meta'sı: "Hedef tarih: — → 20.10.2026", "hata.png ekledi"…
 * Eski kayıtlardaki atama olayları ("talebi üstlendi", "Sorumlu: A → B") da aynı yoldan olduğu gibi gösterilir.
 */
const eventText = (e: DevRequestEvent): React.ReactNode => {
  const who = <b>{drShortName(e.authorName) || "Nedaris"}</b>;
  const meta = e.meta || "";
  if (e.kind === K.Status && e.toStatus) {
    return <>{who} durumu <b>{e.fromStatus ? `${DR_STATUS_LABEL[e.fromStatus]} → ` : ""}{DR_STATUS_LABEL[e.toStatus]}</b> yaptı{meta ? ` · ${meta}` : ""}</>;
  }
  if (e.kind === K.Edit) return <>{who} talebi düzenledi{meta ? ` · ${meta}` : ""}</>;
  if (!meta) return who;
  return <>{who}{startsLower(meta) ? " " : " · "}{meta}</>;
};

const Bubble: React.FC<{ e: DevRequestEvent; head: boolean }> = ({ e, head }) => (
  <div className={`dr-msg${e.isMine ? " mine" : ""}${e.byHandler ? " handler" : ""}${e.isInternal ? " internal" : ""}${e.byVendor ? " vendor" : ""}`}>
    {head && (
      <div className="dr-msg-head">
        <span className="po-mark" style={{ ["--po-hue" as any]: drHue(e.authorName) }} aria-hidden="true">{drInitials(e.authorName)}</span>
        <b>{e.authorName}</b>
        <span className="dr-msg-tag">{e.isInternal ? "İç not · talep sahibi görmez" : e.byVendor ? "Nedaris Destek" : e.byHandler ? "Destek" : "Talep sahibi"}</span>
        {when(e.createdAt)}
      </div>
    )}
    <div className="dr-msg-body">{e.body}</div>
  </div>
);

const DevRequestDrawer: React.FC<DevRequestDrawerProps> = ({ id, preview, refreshKey, escDisabled, drafts, onClose, onChanged, onEdit, onAction, onDelete }) => {
  const navigate = useNavigate();
  const { hasScreen } = usePermissions();
  const [d, setD] = useState<DevRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [attDel, setAttDel] = useState<DevRequestAttachment | null>(null);
  const [fileErr, setFileErr] = useState("");
  const [over, setOver] = useState(false);
  const [ups, setUps] = useState<Job[]>([]);
  const [draftState, setDraftState] = useState<Draft>(() => ({ id, text: "", internal: false, ...drafts.get(id) }));
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sendErr, setSendErr] = useState<{ id: string; msg: string } | null>(null);
  // Açan öğe ilk render'da (odak çekmeceye geçmeden) yakalanır
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const gen = useRef(0);
  const notified = useRef<string | null>(null);
  const upsRef = useRef<Job[]>([]);
  const queue = useRef<{ key: string; reqId: string; file: File }[]>([]);
  const pumping = useRef(false);
  const onChangedRef = useRef(onChanged);
  useEffect(() => { onChangedRef.current = onChanged; });

  const load = useCallback(async () => {
    const g = ++gen.current;
    setLoading(true); setError("");
    try {
      const x = await devRequestService.get(id);
      if (g === gen.current) { setD(x); setMissing(false); }
    } catch (e: any) {
      if (g !== gen.current) return;
      setD(null);
      if (e?.response?.status === 404) {
        setMissing(true);
        // Sayfa listeyi yeniler ve refreshKey'i artırır; aynı talep için tek kez (yoksa 404 → yenile → 404 döngüsü)
        if (notified.current !== id) { notified.current = id; onChangedRef.current(NOT_FOUND); }
      } else { setMissing(false); setError(drErr(e, "Talep yüklenemedi.")); }
    } finally { if (g === gen.current) setLoading(false); }
  }, [id]);
  // Başka bir talebe geçilince eski kart gösterilmesin; aynı talepte yenilemede eski içerik kalır
  useEffect(() => {
    setD((x) => (x && x.devRequestId.toLowerCase() === id.toLowerCase() ? x : null));
    setLightbox(null); setFileErr(""); setOver(false);
  }, [id]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // aria-modal kart odağı alır (derin bağlantıda da); kapanınca odak açıldığı yere döner
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  useEffect(() => {
    const el = asideRef.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Dosya kaldırma onayı açıkken Esc yalnız onu kapatır (ConfirmModal window'da dinler, bu document'ta önce çalışır)
      if (e.key === "Escape" && !e.defaultPrevented && !escDisabled && !attDel && !drLayerBlocked(overlayRef.current)) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, escDisabled, attDel]);

  // ───── Yanıt taslağı: her değişiklik sayfanın drafts sözlüğüne de yazılır ─────
  const draft: Draft = draftState.id === id ? draftState : { id, text: "", internal: false, ...drafts.get(id) };
  const setComposer = (patch: Partial<Omit<Draft, "id">>) => {
    const next: Draft = { ...draft, ...patch, id };
    setDraftState(next);
    if (next.text || next.internal) drafts.set(id, { text: next.text, internal: next.internal }); else drafts.delete(id);
    if (sendErr) setSendErr(null);
  };
  const internalOn = draft.internal && !!d?.actions.canInternal;
  const composerOn = !!d?.actions.canComment;

  // CSS min-height'tan başlar (masaüstü ~3, telefonda ~2 satır), 8 satıra kadar büyür; sonrası kaydırılır
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight) || 20;
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const bd = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const max = lh * 8 + pad;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, max) + bd}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [draft.text, composerOn]);

  const send = async () => {
    if (!d || sendingId) return;
    const body = draft.text.trim();
    if (!body) { setSendErr({ id, msg: "Mesaj boş olamaz." }); taRef.current?.focus(); return; }
    const key = id, reqId = d.devRequestId, asInternal = internalOn;
    setSendingId(key); setSendErr(null);
    try {
      const ev = await devRequestService.comment(reqId, body, asInternal);
      // Taslak temizlenir; iç not kipi korunur (yanlışlıkla herkese açık yazılmasın)
      if (asInternal) drafts.set(key, { text: "", internal: true }); else drafts.delete(key);
      setDraftState((x) => (x.id === key ? { id: key, text: "", internal: asInternal } : x));
      setD((x) => (x && x.devRequestId === reqId ? { ...x, events: x.events.filter((y) => y.eventId !== ev.eventId).concat(ev) } : x));
      window.requestAnimationFrame(() => { const b = bodyRef.current; if (b) b.scrollTop = b.scrollHeight; });
      onChangedRef.current();
    } catch (e: any) {
      // 409/400: talep bu arada değişti ya da kapandı (ör. reddedildi) → kart yenilenir, taslak yerinde kalır
      const st = e?.response?.status;
      if (st === 409 || st === 400) onChangedRef.current();
      setSendErr({ id: key, msg: drErr(e, "Mesaj gönderilemedi.") });
    } finally { setSendingId(null); }
  };

  // ───── Dosyalar: tek tek, sırayla yüklenir (dosya başına ilerleme); her başarıda kutucuk gerçeğine dönüşür ─────
  const patchUps = (fn: (l: Job[]) => Job[]) => { upsRef.current = fn(upsRef.current); setUps(upsRef.current); };
  const pump = async () => {
    if (pumping.current) return;
    pumping.current = true;
    let done = 0;
    try {
      while (queue.current.length) {
        const j = queue.current.shift()!;
        try {
          // %100'e sunucu yanıtıyla varılır (bayt gitti ama kayıt sürüyor olabilir)
          const a = await devRequestService.upload(j.reqId, j.file, (pct) => patchUps((l) => l.map((u) => (u.key === j.key ? { ...u, progress: Math.min(pct, 99) } : u))));
          done++;
          patchUps((l) => l.filter((u) => u.key !== j.key));
          setD((x) => (x && x.devRequestId === j.reqId ? { ...x, attachments: x.attachments.filter((y) => y.attachmentId !== a.attachmentId).concat(a) } : x));
        } catch (e) {
          patchUps((l) => l.map((u) => (u.key === j.key ? { ...u, error: upErr(e) } : u)));
        }
      }
    } finally { pumping.current = false; }
    if (done) onChangedRef.current();
  };
  const addFiles = (files: File[], fromComposer = false) => {
    if (!d || !files.length) return;
    if (!d.actions.canAttach) {
      // Açık talepte ekleme yetkisi olmayan (yalnız görüntüleyen) için sessiz geç; kapalı talepte sunucunun metni
      if (!drIsOpen(d.status)) { if (fromComposer) setSendErr({ id, msg: CLOSED_FILES }); else setFileErr(CLOSED_FILES); }
      return;
    }
    setFileErr("");
    const reqId = d.devRequestId;
    const live = upsRef.current.filter((u) => u.reqId === reqId && !u.error);
    let count = d.attachments.length + live.length;
    let total = d.attachments.reduce((s, a) => s + a.size, 0) + live.reduce((s, u) => s + u.size, 0);
    const added: Job[] = files.map((file) => {
      const key = `dru${++upSeq}`;
      const err = drCheckFile(file)
        || (count + 1 > DR_MAX_FILES ? DR_FILE_MSG.count : null)
        || (total + file.size > DR_MAX_TOTAL ? DR_FILE_MSG.total : null);
      if (!err) { count++; total += file.size; queue.current.push({ key, reqId, file }); }
      return { key, reqId, name: file.name, size: file.size, progress: 0, error: err || undefined };
    });
    patchUps((l) => l.concat(added));
    pump();
  };
  const pickFiles = () => fileRef.current?.click();

  const removeAttachment = async () => {
    const a = attDel;
    setAttDel(null);
    if (!a) return;
    setFileErr("");
    try {
      await devRequestService.removeAttachment(a.attachmentId);
      setD((x) => (x ? { ...x, attachments: x.attachments.filter((y) => y.attachmentId !== a.attachmentId) } : x));
      onChangedRef.current();
    } catch (e) {
      setFileErr(drErr(e, "Dosya kaldırılamadı."));
    }
    // Kaldırılan kutucuğun düğmesi yok oldu: odak çekmecede kalsın (Esc çalışmaya devam eder)
    asideRef.current?.focus({ preventScroll: true });
  };
  const closeLightbox = useCallback(() => setLightbox(null), []);

  const h: DevRequestListItem | null = d ?? preview ?? null;
  const ti = h ? DR_TYPE[h.type] ?? DR_TYPE[T.Question] : null;
  const hOpen = !!h && drIsOpen(h.status);
  const waitDays = h && h.status === S.NeedsInfo ? daysSince(h.statusChangedAt) ?? 0 : 0;

  // ───── Gövde hesapları (yalnız ayrıntı yüklüyken) ─────
  const handler = !!d && d.actions.canInternal;
  const open = !!d && drIsOpen(d.status);
  const myUps = d ? ups.filter((u) => u.reqId === d.devRequestId) : [];
  const upCount = myUps.filter((u) => !u.error).length;
  let acts: React.ReactNode[] = [];
  if (d) {
    const tr = d.actions.transitions || [];
    const can = (s: S) => tr.indexOf(s) >= 0;
    const btn = (key: string, cls: string, icon: React.ReactNode, label: string, fn: () => void) =>
      <button type="button" key={key} className={`po-btn small ${cls}`} onClick={fn}>{icon} {label}</button>;
    // Yöneticide not satırın solunda durur ve düğmeleri sağa iter; diğerlerinde ilk yıkıcı düğme sağa yaslanır
    let pushed = handler;
    const push = () => { const p = pushed; pushed = true; return p ? "" : " push"; };
    if (handler) acts.push(<span key="managed" className="po-hint dr-managed">{I.info}{DR_VENDOR_MANAGED}</span>);
    if (d.actions.canEdit) acts.push(btn("edit", "", I.edit, "Düzenle", () => onEdit(d)));
    // Talep sahibinin işlemleri (yönetici de kendi talebinde bunları alır; sunucunun transitions'ı belirler)
    if (d.status === S.Done && can(S.InReview)) acts.push(btn("reopen", "", I.reopen, drTransitionLabel(d.status, S.InReview), () => onAction(d, S.InReview)));
    if (can(S.Withdrawn)) acts.push(btn("withdraw", `danger${push()}`, I.withdraw, drTransitionLabel(d.status, S.Withdrawn), () => onAction(d, S.Withdrawn)));
    if (d.actions.canDelete) acts.push(btn("del", `danger${push()}`, I.trash, "Sil", () => onDelete(d)));
  }
  const si = d ? drStepIndex(d.status) : -1;
  const stDays = d ? daysSince(d.statusChangedAt) ?? 0 : 0;
  const left = d ? drDaysUntil(d.targetDate) : null;
  const tState = left == null || !open ? "" : left < 0 ? "late" : left <= 3 ? "soon" : "";
  const withinReopen = !!d && (daysSince(d.closedAt) ?? DR_REOPEN_DAYS) < DR_REOPEN_DAYS;
  const goPath = d && d.screenCode && hasScreen(d.screenCode) ? screenPath(d.screenCode) : null;
  const dropProps = d && d.actions.canAttach ? {
    onDragEnter: (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); setOver(true); },
    onDragOver: (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setOver(true); },
    onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); },
    onDrop: (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); setOver(false); addFiles(Array.from(e.dataTransfer.files)); },
  } : {};
  const myErr = sendErr && sendErr.id === id ? sendErr.msg : "";
  const sending = sendingId === id;

  return (
    <>
      <div className="po-overlay" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        // Dosya alanı dışına bırakılan dosya tarayıcıda açılıp uygulamadan çıkarmasın
        onDragOver={(e) => { if (hasFiles(e) && !e.nativeEvent.defaultPrevented) { e.preventDefault(); e.dataTransfer.dropEffect = "none"; } }}
        onDrop={(e) => { if (hasFiles(e)) e.preventDefault(); }}>
        <aside className="po-drawer" ref={asideRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="po-dr-title"
          onPaste={(e) => {
            if (!d || lightbox != null) return;
            const t = e.target as HTMLElement | null;
            // Excel/Word'den kopyalanan metin yanında görsel de taşır: metin alanında metin yapıştırılsın
            if (t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT") && (e.clipboardData.getData("text/plain") || "").trim()) return;
            const files = drPastedFiles(e.clipboardData);
            if (!files.length) return;
            e.preventDefault();
            addFiles(files, !!t && !!composerRef.current?.contains(t));
          }}>
          <div className="po-drawer-head">
            <div className="po-drawer-title">
              {h && ti && <span className="po-mark big" style={{ ["--po-hue" as any]: ti.hue }} aria-hidden="true">{ti.glyph}</span>}
              <div>
                <span className="po-kicker">{h && ti ? `${ti.label} · ${h.screenName || "Genel"} · ${relTime(h.createdAt)}` : "Geliştirme talebi"}</span>
                <h2 id="po-dr-title">{h?.number ?? "Talep"}</h2>
                {h && <div className="po-drawer-sup" title={h.title}>{h.title}</div>}
                {h && (
                  <div className="po-badges">
                    <span className={`po-st ${DR_STATUS_CLASS[h.status] ?? ""}`}>{DR_STATUS_LABEL[h.status]}</span>
                    {h.priority != null && <span className={`po-badge ${DR_PRIORITY_BADGE[h.priority]}`}>Öncelik: {DR_PRIORITY_LABEL[h.priority]}</span>}
                    {hOpen && h.impact === Im.Blocking && <span className="po-badge err">İşi durduruyor</span>}
                    {h.status === S.NeedsInfo && <span className={`po-badge ${waitDays >= DR_WAIT_DAYS ? "err" : "warn"}`}>Bilgi bekleniyor · {waitDays} gün</span>}
                  </div>
                )}
              </div>
            </div>
            <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
          </div>

          <div className="po-drawer-body" ref={bodyRef}>
            {!d && loading && <div className="po-loading"><span className="po-spin" /> Talep yükleniyor</div>}

            {!d && !loading && (
              <div className="po-blank">
                <span className="po-blank-icon">{I.inbox}</span>
                <b>{missing ? "Talep bulunamadı" : "Talep yüklenemedi"}</b>
                {missing ? "Silinmiş ya da erişiminiz yok." : error || "Bağlantıyı kontrol edip yeniden deneyin."}
                <div className="po-actrow" style={{ justifyContent: "center" }}>
                  <button type="button" className="po-btn" onClick={load}>Yeniden dene</button>
                  <button type="button" className="po-btn primary" onClick={onClose}>Listeye dön</button>
                </div>
              </div>
            )}

            {d && (
              <>
                {/* İşlemler (sunucunun actions'ı belirler) */}
                {acts.length > 0 && <div className="po-actrow">{acts}</div>}

                {/* Adımlar */}
                {si >= 0 && (
                  <div>
                    <ol className="dr-steps" aria-label="Talep durumu">
                      {DR_STEPS.map(([s, label], i) => (
                        <li key={s} className={`dr-step ${i < si ? "done" : i === si ? (d.status === S.NeedsInfo ? "warn" : "cur") : ""}`} aria-current={i === si ? "step" : undefined}>{label}</li>
                      ))}
                    </ol>
                    <span className="dr-steps-text">{si + 1}/{DR_STEPS.length} · {DR_STATUS_LABEL[d.status]}</span>
                    <p className="po-hint" style={{ marginTop: "0.55rem" }}>{DR_STATUS_HINT[d.status]}{d.status === S.Planned && d.targetDate ? ` · hedef ${drFmtDate(d.targetDate)}` : ""}</p>
                  </div>
                )}

                {/* Durum notu */}
                {d.status === S.NeedsInfo && (
                  <div className="po-note warn"><b>Bilgi bekleniyor:</b> <span style={PRE}>{d.pendingQuestion}</span>{d.isMine && " Yanıtınızı aşağıya yazın; talep yeniden incelemeye döner."}</div>
                )}
                {d.status === S.Done && (
                  <div className="po-note info"><b>Tamamlandı · {drFmtDate(d.closedAt)}</b>{d.resolution && <span style={PRE}>{` — ${d.resolution}`}</span>}{d.isMine && withinReopen && " Sorun sürüyorsa 30 gün içinde yeniden açabilirsiniz."}</div>
                )}
                {d.status === S.Rejected && <div className="po-note err"><b>Reddedildi:</b> <span style={PRE}>{d.resolution}</span></div>}
                {d.status === S.Withdrawn && <div className="po-note">Talep sahibi talebi geri çekti.</div>}

                {/* Rakamlar */}
                <div className="po-figs dr-figs2">
                  <div className="po-fig"><span className="l">Durum</span><span className="v">{DR_STATUS_LABEL[d.status]}</span><span className="s">{stDays > 0 ? `${stDays} gündür bu durumda` : "bugünden beri bu durumda"}</span></div>
                  <div className={`po-fig ${tState}`}><span className="l">Hedef tarih</span><span className="v">{d.targetDate ? drFmtDate(d.targetDate) : "—"}</span>
                    {left != null && open && <span className="s">{left > 0 ? `${left} gün kaldı` : left === 0 ? "bugün" : `${-left} gün geçti`}</span>}</div>
                </div>

                {/* Künye */}
                <dl className="po-facts">
                  <div><dt>Talep eden</dt><dd>{d.requesterName}<small>{drFmtDateTime(d.createdAt)}</small></dd></div>
                  <div><dt>Ekran</dt><dd>{d.screenName || "Genel"}{goPath && <small><button type="button" className="po-link" onClick={() => navigate(goPath)}>Ekrana git</button></small>}</dd></div>
                  <div><dt>Tür</dt><dd>{ti?.label}</dd></div>
                  <div><dt>Etki</dt><dd>{DR_IMPACT_LABEL[d.impact] ?? "—"}</dd></div>
                  {handler && d.priority != null && <div><dt>Öncelik</dt><dd><span className={`po-badge ${DR_PRIORITY_BADGE[d.priority]}`}>{DR_PRIORITY_LABEL[d.priority]}</span></dd></div>}
                  {d.updatedAt && <div><dt>Son güncelleme</dt><dd>{drFmtDateTime(d.updatedAt)}<small>{d.updatedBy || "—"}</small></dd></div>}
                </dl>

                {/* Açıklama: düz metin, React kaçışlar (HTML olarak işlenmez) */}
                <section className="po-sec">
                  <h3>Açıklama</h3>
                  <div className="dr-desc">{d.description}</div>
                </section>

                {/* Dosyalar */}
                <section className="po-sec" {...dropProps} style={over ? DROP_OVER : undefined}>
                  <h3>Dosyalar <em>{d.attachments.length}</em>
                    {d.actions.canAttach && <button type="button" className="po-link" style={H3_BTN} onClick={pickFiles}>Dosya ekle</button>}
                  </h3>
                  <DrAttachmentGrid items={d.attachments} uploading={myUps} onOpen={setLightbox} onDelete={setAttDel}
                    onError={setFileErr} onDismissUpload={(key) => patchUps((l) => l.filter((u) => u.key !== key))}
                    emptyText={d.actions.canAttach ? "Henüz dosya yok. Dosyayı buraya sürükleyin ya da ekran görüntüsünü Ctrl+V ile yapıştırın." : undefined} />
                  {fileErr && <span className="po-fielderr" role="alert">{fileErr}</span>}
                </section>

                {/* Yazışma ve geçmiş (eskiden yeniye) */}
                <section className="po-sec">
                  <h3>Yazışma ve geçmiş <em>{d.events.length}</em></h3>
                  <div className="dr-thread">
                    <div className="dr-event"><span><b>{d.requesterName}</b> talebi oluşturdu · {when(d.createdAt)}</span></div>
                    {d.events.map((e) => (e.kind === K.Comment ? <Bubble key={e.eventId} e={e} head /> : (
                      <React.Fragment key={e.eventId}>
                        <div className={`dr-event${e.isInternal ? " internal" : ""}`}><span>{eventText(e)} · {when(e.createdAt)}</span></div>
                        {e.body && <Bubble e={e} head={false} />}
                      </React.Fragment>
                    )))}
                  </div>
                  {!composerOn && d.isMine && !open && <p className="po-hint">Talep kapalı. Sorun sürüyorsa yeniden açın ya da yeni talep açın.</p>}
                </section>

                {/* Yanıt alanı (gövdenin altına yapışık) */}
                {composerOn && (
                  <div className={`dr-composer${internalOn ? " internal" : ""}`} ref={composerRef}>
                    <textarea ref={taRef} rows={1} aria-label="Mesaj" maxLength={MSG_MAX} value={draft.text} readOnly={sending}
                      placeholder={internalOn ? "İç not yazın; talep sahibi görmez…" : "Yanıt yazın…"}
                      onChange={(e) => setComposer({ text: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }} />
                    {myErr && <span className="po-fielderr" role="alert">{myErr}</span>}
                    {d.isMine && d.status === S.NeedsInfo && !internalOn && <p className="po-hint">Yanıtınız talebi yeniden incelemeye gönderir.</p>}
                    <div className="dr-composer-foot">
                      {d.actions.canAttach && <button type="button" className="po-ib" aria-label="Dosya ekle" title="Dosya ekle" onClick={pickFiles}>{I.clip}</button>}
                      {d.actions.canInternal && (
                        <div className="po-seg" role="radiogroup" aria-label="Mesaj türü">
                          <button type="button" role="radio" aria-checked={!internalOn} className={internalOn ? "" : "on"} onClick={() => setComposer({ internal: false })}>Yanıt</button>
                          <button type="button" role="radio" aria-checked={internalOn} className={internalOn ? "on" : ""} onClick={() => setComposer({ internal: true })}>İç not</button>
                        </div>
                      )}
                      {upCount > 0 && <span className="po-hint">{upCount} dosya yükleniyor…</span>}
                      <span className="po-counter">{draft.text.length}/{MSG_MAX}</span>
                      <button type="button" className="po-btn small primary" title="Ctrl+Enter" disabled={sending} onClick={send}>{I.send} {sending ? "Gönderiliyor…" : internalOn ? "İç not ekle" : "Gönder"}</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <input ref={fileRef} type="file" multiple accept={DR_ACCEPT} hidden tabIndex={-1} onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
        </aside>
        {d && lightbox != null && <DrLightbox items={d.attachments} start={lightbox} onClose={closeLightbox} />}
      </div>
      <ConfirmModal isOpen={!!attDel} type="danger" title="Dosyayı kaldır" message={`${attDel?.fileName ?? ""} talepten kaldırılacak.`}
        confirmText="Kaldır" cancelText="Vazgeç" onConfirm={removeAttachment} onCancel={() => setAttDel(null)} />
    </>
  );
};

export default DevRequestDrawer;
